import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import {
  Container,
  Grid,
  Typography,
  Modal,
  Button,
  Divider,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Paper,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Alert
} from "@mui/material";
import {
  InfoOutlined,
  ExpandMore,
  ExpandLess,
  CheckCircleOutline,
  ErrorOutline
} from "@mui/icons-material";
import { PhoneInput } from "react-international-phone";
import ReCAPTCHA from "react-google-recaptcha";
import "react-international-phone/style.css";

// Contextos y acciones
import { PlansAndServicesContext } from "../../contexts/contex-plans-and-services";
import { useCookieContext } from "../cookiesModal/cookie-context.jsx";
import { saveCompany } from "../../actions/company.js";
import { consultCompanies } from "../../actions/plans";
import { demoRequest, generateCodePreRegistration } from "../../actions/users.js";
import { errors as errorsFront } from "../../config/errors";

// Componentes modales
import ModalDialogComponent from "../../components/dialog/dialog-component";
import ModalRegistration from "./modal-registration";
import ModalTermsAndConditions from "./modal-terms-and-conditions";
import ModalTreatmentPersonalData from "./modal-treatment-personal-data";
import ModalValidateCode from "../modal-validation/modal-validation.jsx";

// Estilos personalizados
const CustomButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #7220db 0%, #9c27b0 100%)",
  transition: "all 0.3s ease",
  color: theme.palette.common.white,
  borderRadius: 8,
  fontWeight: 700,
  padding: theme.spacing(1.5, 4),
  fontSize: 16,
  textTransform: "none",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)"
  },
  "&.Mui-disabled": {
    background: theme.palette.grey[400]
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    "& fieldset": {
      borderColor: theme.palette.grey[300]
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1
    }
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  marginBottom: theme.spacing(4)
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.light,
  borderRadius: 8,
  marginBottom: theme.spacing(3),
  "& svg": {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
    fontSize: "2rem"
  }
}));

function getSmallDateTime() {
  return new Date().toISOString();
}

function RegisterForm() {
  const { t, i18next } = useTranslation("global");
  const language = localStorage.getItem("i18nextLng") || "es";
  const { setOpenAlert, setMessageAlert, setOpenLoader } = useContext(PlansAndServicesContext);
  const { isCookieActive } = useCookieContext();

  // Estados del formulario
  const [data, setData] = useState({
    names: "",
    lastname: "",
    email: "",
    phone: "",
    company: "",
    platform: ""
  });
  
  const [errorData, setErrorData] = useState({});
  const [checked, setChecked] = useState(false);
  const [checkedTwo, setCheckedTwo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isValidSelection, setIsValidSelection] = useState(false);
  
  // Estados para modales
  const [openModal, setOpenModal] = useState(false);
  const [openValidateCodeModal, setOpenValidateCodeModal] = useState(false);
  const [modalDialogComponentLoad, setModalDialogComponentLoad] = useState(false);
  const [tittleError, setTittleError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [dataSend, setDataSend] = useState({});
  const [resolveHandler, setResolveHandler] = useState(null);
  
  // Estados para modal de nueva empresa
  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  const [newCompany, setNewCompany] = useState({ nit: "", name: "" });
  const [captchaValid, setCaptchaValid] = useState(false);
  
  // Estados para sección colapsable
  const [expanded, setExpanded] = useState(false);

  // Expresiones regulares
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneNumberRegex = /^\+\d{11}$/;

  // Opciones de plataforma
  const listApp = [
    { value: "PowerFactory" },
    { value: "PSSE" },
    { value: t("demo.both") }
  ];

  // Cargar empresas al montar el componente
  useEffect(() => {
    const fetchCompanies = async () => {
      const response = await consultCompanies();
      if (response.status === 200) setCompanies(response.response);
    };
    fetchCompanies();
  }, []);

  // Manejadores de eventos
  const handleChange = (name) => (event) => {
    const value = event?.target?.value ?? event;
    setData(prev => ({ ...prev, [name]: value }));
    setErrorData(prev => ({ ...prev, [name]: "" }));
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value.toUpperCase();
    handleChange("company")(value);
    setIsValidSelection(false);

    if (value) {
      const filtered = companies.filter(ele => 
        ele.toUpperCase().includes(value.toUpperCase())
      );
      setFilteredCompanies(filtered);
      setShowDropdown(true);
    } else {
      setFilteredCompanies([]);
      setShowDropdown(false);
    }
  };

  const handleSelectCompany = (value) => {
    const valueUpperCase = value.toUpperCase();
    handleChange("company")(valueUpperCase);
    setFilteredCompanies([]);
    setShowDropdown(false);
    setIsValidSelection(true);
  };

  const handleCheckboxChange = (event) => {
    setChecked(event.target.checked);
    setErrorData(prev => ({ ...prev, checkOne: "" }));
  };

  const handleCheckboxChangeTwo = (event) => {
    setCheckedTwo(event.target.checked);
    setErrorData(prev => ({ ...prev, checkTwo: "" }));
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  // Manejadores para modal de nueva empresa
  const handleOpenCompanyModal = () => setOpenCompanyModal(true);
  const handleCloseCompanyModal = () => setOpenCompanyModal(false);

  const handleNewCompanyChange = (field) => (event) => {
    setNewCompany(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValid(!!value);
  };

  const handleRegisterCompany = async () => {
    if (!newCompany.name || !newCompany.nit) {
      setMessageAlert(t(`RegisterCompany.Error.error1`));
      setOpenAlert(true);
      return;
    }

    const companyData = { 
      name: newCompany.name.toUpperCase(), 
      nit: newCompany.nit.toUpperCase()
    };

    try {
      const response = await saveCompany(companyData);
      if (response.status === 201 || response.status === 200) {
        setMessageAlert(t(`RegisterCompany.Error.error2`));
        setOpenAlert(true);
        handleChange("company")(companyData.name);
        
        const companiesResponse = await consultCompanies();
        if (companiesResponse.status === 200) {
          setCompanies(companiesResponse.response);
        }
        
        setNewCompany({ nit: "", name: "" });
        setCaptchaValid(false);
        handleCloseCompanyModal();
      } else {
        setMessageAlert(t(`RegisterCompany.Error.error3`));
        setOpenAlert(true);
      }
    } catch (error) {
      console.error(t(`RegisterCompany.Error.error4`), error);
      setMessageAlert(t(`RegisterCompany.Error.error5`));
      setOpenAlert(true);
    }
  };

  // Manejador de envío del formulario
  const handleSubmit = async (event) => {
    event.preventDefault();
    let validator = [];
    let dataResponse = {};
    let internalErrorData = {};

    // Validación de campos requeridos
    const requiredFields = ["names", "email", "phone", "company", "lastname", "platform"];
    requiredFields.forEach(key => {
      if (!data[key]) {
        internalErrorData[key] = t(`RegisterCompany.Error.required`);
        validator.push(1);
      } else {
        if (key === "email" && !emailRegex.test(data[key])) {
          internalErrorData[key] = t(`RegisterCompany.Error.invalid`);
          validator.push(1);
        }
        if (key === "phone" && data[key].length < 10) {
          internalErrorData[key] = t(`RegisterCompany.Error.invalid`);
          validator.push(1);
        }
        dataResponse[key] = data[key];
      }
    });

    // Validación de empresa
    const validCompanies = companies.filter(
      ele => ele.toUpperCase() === (data.company || "").toUpperCase()
    );

    if (!validCompanies.length) {
      internalErrorData["company"] = t(`RegisterCompany.Error.invalid`);
      validator.push(1);
    }

    // Validación de checkboxes
    if (!checked) {
      internalErrorData["checkOne"] = t(`RegisterCompany.Error.required`);
      validator.push(1);
    } else {
      dataResponse.terms_and_conditions = checked;
    }

    if (!checkedTwo) {
      internalErrorData["checkTwo"] = t(`RegisterCompany.Error.required`);
      validator.push(1);
    } else {
      dataResponse.treatment_personal_data = checkedTwo;
    }

    setErrorData(internalErrorData);

    if (validator.length === 0) {
      dataResponse.date = getSmallDateTime();
      dataResponse.status = 1;

      try {
        setLoading(true);
        setDataSend(dataResponse);
        
        const codeResponse = await generateCodePreRegistration(dataResponse);
        if (codeResponse.status === 200) {
          if (codeResponse.response !== "email registered") {
            setOpenValidateCodeModal(true);
            await new Promise(resolve => setResolveHandler(() => resolve));
          }

          const demoResponse = await demoRequest(dataResponse);
          if (demoResponse.status === 200) {
            setOpenLoader(false);
            setOpenModal(true);
            setLoading(false);
            setChecked(false);
            setCheckedTwo(false);
          } else {
            setMessageError(t("demo.messageError", { name: demoResponse.error }));
            setTittleError(t("demo.errorTitle"));
            setModalDialogComponentLoad(true);
            setTimeout(() => {
              setLoading(false);
              setModalDialogComponentLoad(false);
            }, 5000);
          }
        }
      } catch (error) {
        errorsFront.addErroLog({
          data: {
            url: window.location,
            error: error.toString(),
            message: error.message,
            file: error.file,
            line: error.line,
          },
        }).then();
        console.error(error);
        setMessageAlert(t("demo.generalError"));
        setOpenAlert(true);
        setLoading(false);
      }
    } else {
      setMessageAlert(t("demo.validationError"));
      setOpenAlert(true);
    }
  };

  return (
    <>
      {/* Componentes modales */}
      <ModalDialogComponent
        view={modalDialogComponentLoad}
        tittle={tittleError}
        message={messageError}
      />

      <ModalValidateCode
        open={openValidateCodeModal}
        data={dataSend}
        onClose={() => setOpenValidateCodeModal(false)}
        onResolve={resolveHandler}
      />

      <ModalRegistration
        open={openModal}
        data={data}
        onClose={() => setOpenModal(false)}
      />

      {/* Modal para registrar nueva empresa */}
      <Modal
        open={openCompanyModal}
        onClose={handleCloseCompanyModal}
        aria-labelledby="add-company-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "600px", md: "800px" },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 3,
            p: 4,
            outline: "none"
          }}
        >
          <Typography
            id="add-company-title"
            variant="h5"
            component="div"
            sx={{
              textAlign: "center",
              mb: 3,
              color: "white",
              fontWeight: "bold",
              background: "linear-gradient(90deg, #3f008e, #7220db 39.39%, #ff6a13 100.59%)",
              p: 2,
              borderRadius: 2
            }}
          >
            {t(`RegisterCompany.Modal.title`)}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              mt: 2
            }}
          >
            <StyledTextField
              label={t(`RegisterCompany.Modal.company`)}
              variant="outlined"
              fullWidth
              required
              value={newCompany.name}
              onChange={handleNewCompanyChange("name")}
            />

            <StyledTextField
              label={t(`RegisterCompany.Modal.nit`)}
              variant="outlined"
              fullWidth
              required
              value={newCompany.nit}
              onChange={handleNewCompanyChange("nit")}
            />

            <ReCAPTCHA
              sitekey="6LefRPYdAAAAANdQTSZ4NA-OV2BmT3dojawQ8-Gf"
              onChange={handleCaptchaChange}
            />

            <CustomButton
              variant="contained"
              disabled={!captchaValid || !newCompany.name || !newCompany.nit}
              onClick={handleRegisterCompany}
              fullWidth
            >
              {t(`RegisterCompany.Modal.register`)}
            </CustomButton>
          </Box>
        </Box>
      </Modal>

        <Container maxWidth="lg" sx={{ py: 2, mt: -8, mb: -4 }}>
          <Grid container spacing={4} marginTop={0}>
          {/* Sección de información */}
          <Grid item xs={12} md={6}>
            <StyledPaper elevation={0} sx={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '87%' 
            }}>
              <HighlightBox 
                sx={{
                  background: "linear-gradient(90deg, #3f008e, #7220db 39.39%, #ff6a13 100.59%)",
                  color: "white",
                }}>
                <InfoOutlined fontSize="large" sx={{ color: "#ffffff !important" }} /> 
                <Typography variant="h5" component="h2" fontWeight="bold">
                  {t(`demo.importantReminder`)}
                </Typography>
              </HighlightBox>

              <Box sx={{ flexGrow: 1 }}> 
                <Typography variant="body1" color="text.secondary" paragraph>
                  {t(`demo.description1`)}
                </Typography>

                <List dense>
                  {[1, 2, 3, 4].map((item) => (
                    <ListItem key={item} sx={{ py: 0 }}>
                      <CheckCircleOutline sx={{ color: "#7220db", mr: 1 }} />
                      <ListItemText
                        primary={t(`demo.downloadInstructions${item}`)}
                        primaryTypographyProps={{ color: "text.secondary" }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="body1" color="text.secondary" paragraph>
                  {t(`demo.description2`)}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t(`demo.additionalInfo1`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t(`demo.additionalInfo2`)}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </StyledPaper>
          </Grid>


          {/* Formulario de registro */}
          <Grid item xs={12} md={6}>
            <StyledPaper elevation={0}>
            <Typography
                  variant="h4"
                  component="h2"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    textAlign: 'center',
                    background: "linear-gradient(90deg, #3f008e, #7220db 39.39%, #ff6a13 100.59%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  >
                  {t(`demo.requestDemo`)}
                  </Typography>

              <Divider sx={{ my: 2 }} />

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Nombre */}
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      label={t(`demo.firstName`)}
                      variant="outlined"
                      fullWidth
                      required
                      value={data.names}
                      onChange={handleChange("names")}
                      error={!!errorData.names}
                      helperText={errorData.names}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#7220db',
                            transition: 'border-color 0.3s ease',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7220db',
                            transition: 'border-color 0.3s ease',
                          },
                        },
                        '& label': {
                          '&.Mui-focused': {
                            color: '#7220db',
                            transition: 'color 0.3s ease',
                          },
                          '&:hover': {
                            color: '#7220db',
                            transition: 'color 0.3s ease',
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Apellido */}
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      label={t(`demo.lastName`)}
                      variant="outlined"
                      fullWidth
                      required
                      value={data.lastName}
                      onChange={handleChange("lastname")}
                      error={!!errorData.lastName}
                      helperText={errorData.lastName}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#7220db',
                            transition: 'border-color 0.3s ease',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7220db',
                            transition: 'border-color 0.3s ease',
                          },
                        },
                        '& label': {
                          '&.Mui-focused': {
                            color: '#7220db',
                            transition: 'color 0.3s ease',
                          },
                          '&:hover': {
                            color: '#7220db',
                            transition: 'color 0.3s ease',
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12}>
                    <StyledTextField
                      label={t(`demo.email`)}
                      variant="outlined"
                      fullWidth
                      required
                      type="email"
                      value={data.email}
                      onChange={handleChange("email")}
                      error={!!errorData.email}
                      helperText={errorData.email}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#7220db',
                            transition: 'border-color 0.3s ease',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7220db',
                            transition: 'border-color 0.3s ease',
                          },
                        },
                        '& label': {
                          '&.Mui-focused': {
                            color: '#7220db',
                            transition: 'color 0.3s ease',
                          },
                          '&:hover': {
                            color: '#7220db',
                            transition: 'color 0.3s ease',
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Teléfono */}
                  <Grid item xs={12}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="caption" display="block" gutterBottom>
                        {t(`demo.phone`)} *
                      </Typography>
                      <PhoneInput
                        defaultCountry="co"
                        value={data.phone}
                        onChange={handleChange("phone")}
                        style={{ width: '100%' }}
                        inputStyle={{
                          width: '100%',
                          borderRadius: '8px',
                          borderColor: errorData.phone ? '#f44336' : '#ccc',
                          padding: '16.5px 14px'
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#7220db',
                              transition: 'border-color 0.3s ease',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#7220db',
                              transition: 'border-color 0.3s ease',
                            },
                          },
                          '& label': {
                            '&.Mui-focused': {
                              color: '#7220db',
                              transition: 'color 0.3s ease',
                            },
                            '&:hover': {
                              color: '#7220db',
                              transition: 'color 0.3s ease',
                            },
                          },
                        }}
                      />
                      {errorData.phone && (
                        <Typography variant="caption" color="error">
                          {errorData.phone}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Empresa */}
                  <Grid item xs={12} sm={8}>
                    <Box sx={{ position: 'relative' }}>
                      <StyledTextField
                        label={t(`demo.company`)}
                        variant="outlined"
                        fullWidth
                        required
                        value={data.company}
                        onChange={handleCompanyChange}
                        onFocus={() => {
                          if (data.company) {
                            const filtered = companies.filter(ele =>
                              ele.toUpperCase().includes(data.company.toUpperCase())
                            );
                            setFilteredCompanies(filtered);
                          }
                          setShowDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        error={!!errorData.company}
                        helperText={errorData.company}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#7220db',
                              transition: 'border-color 0.3s ease',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#7220db',
                              transition: 'border-color 0.3s ease',
                            },
                          },
                          '& label': {
                            '&.Mui-focused': {
                              color: '#7220db',
                              transition: 'color 0.3s ease',
                            },
                            '&:hover': {
                              color: '#7220db',
                              transition: 'color 0.3s ease',
                            },
                          },
                        }}
                      />

                      {showDropdown && filteredCompanies.length > 0 && (
                        <Paper
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 10,
                            maxHeight: 200,
                            overflow: 'auto',
                            mt: 0.5,
                            boxShadow: 3
                          }}
                        >
                          <List dense>
                            {filteredCompanies
                              .filter((ele, index, self) =>
                                self.findIndex(
                                  item => item.toUpperCase() === ele.toUpperCase()
                                ) === index
                              )
                              .map((ele, index) => (
                                <ListItem
                                  key={`${ele}-${index}`}
                                  button
                                  onClick={() => handleSelectCompany(ele)}
                                  sx={{
                                    '&:hover': {
                                      bgcolor: 'action.hover'
                                    }
                                  }}
                                >
                                  <ListItemText primary={ele.toUpperCase()} />
                                </ListItem>
                              ))}
                          </List>
                        </Paper>
                      )}
                    </Box>
                  </Grid>

                  {/* Plataforma */}
                  <Grid item xs={12} sm={4}>
                  <StyledTextField
                    select
                    label={t(`demo.application`)}
                    variant="outlined"
                    fullWidth
                    required
                    value={data.platform}
                    onChange={handleChange("platform")}
                    error={!!errorData.platform}
                    helperText={errorData.platform}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#7220db',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#7220db',
                        },
                      },
                      '& label.Mui-focused': {
                        color: '#7220db',
                      },
                    }}
                  >
                    {listApp.map((option) => (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(114, 32, 219, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(114, 32, 219, 0.2)', 
                            '&:hover': {
                              backgroundColor: 'rgba(114, 32, 219, 0.25)', 
                            },
                          },
                        }}
                      >
                        {option.value}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                  </Grid>

                  {/* Mensaje si la empresa no existe */}
                  {data.company && filteredCompanies.length === 0 && !isValidSelection && (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {t(`RegisterCompany.message1`)}
                        <Button 
                          color="primary" 
                          onClick={handleOpenCompanyModal}
                          sx={{ ml: 1, textTransform: 'none' }}
                        >
                          {t(`RegisterCompany.message2`)}
                        </Button>
                      </Alert>
                    </Grid>
                  )}

                  {/* Checkboxes de términos */}
                  <Grid item xs={12} sx={{ py: 0, my: 0 }}> 
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={handleCheckboxChange}
                          sx={{
                            color: '#7220db',
                            '&.Mui-checked': {
                              color: '#7220db',
                            },
                            padding: '4px', 
                            marginRight: '8px', 
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" component="span" sx={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          flexWrap: 'wrap',
                          marginLeft: '-4px' 
                        }}>
                          {t(`demo.accept`)} <ModalTermsAndConditions /> *
                          {errorData.checkOne && (
                            <Typography component="span" color="error" variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                              {" "}{errorData.checkOne}
                            </Typography>
                          )}
                        </Typography>
                      }
                      sx={{ 
                        alignItems: 'center',
                        margin: 0,
                        padding: 0 
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ py: 0, my: 0 }}> 
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkedTwo}
                          onChange={handleCheckboxChangeTwo}
                          sx={{
                            color: '#7220db',
                            '&.Mui-checked': {
                              color: '#7220db',
                            },
                            padding: '4px', 
                            marginRight: '8px', 
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" component="span" sx={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          flexWrap: 'wrap',
                          marginLeft: '-4px' 
                        }}>
                          {t(`demo.consentAuthorize`)} <ModalTreatmentPersonalData /> *
                          {errorData.checkTwo && (
                            <Typography component="span" color="error" variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                              {" "}{errorData.checkTwo}
                            </Typography>
                          )}
                        </Typography>
                      }
                      sx={{ 
                        alignItems: 'center',
                        margin: 0, 
                        padding: 0 
                      }}
                    />
                  </Grid>

                  {/* Botón de envío */}
                  <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                    <CustomButton
                      {...(isCookieActive && { 'data-gtm-id': 'request-demo-form' })}
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {t(`demo.request`)}
                    </CustomButton>
                  </Grid>
                </Grid>
              </form>
            </StyledPaper>
          </Grid>
        </Grid>
      </Container>

    </>
  );
}

export default RegisterForm;